import type { BuscarCadastro, CadastroEncontrado } from '@mymaxx/shared';
import { Aviso, Botao, Modal } from '@mymaxx/ui';
import { useMutation } from '@tanstack/react-query';
import { Search, Wifi } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { ErroApi } from '../lib/api';
import { atendimentoApi } from '../lib/atendimento-api';

type Por = 'documento' | 'codigo';

/**
 * Resgate para quando a triagem não identificou o cliente: o atendente procura o cadastro
 * no ERP pelo CPF/CNPJ ou pelo código, confere se é a pessoa certa e vincula. A busca é
 * separada da confirmação de propósito — ninguém vincula sem olhar quem apareceu.
 */
export function ModalVincular({
  conversaId,
  onFechar,
  onVinculado,
}: {
  conversaId: string;
  onFechar: () => void;
  onVinculado: () => void;
}) {
  const [por, setPor] = useState<Por>('documento');
  const [valor, setValor] = useState('');
  const [encontrado, setEncontrado] = useState<CadastroEncontrado | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const criterio = (): BuscarCadastro =>
    por === 'documento'
      ? { documento: valor.replace(/\D/g, '') }
      : { hubsoftClienteId: Number(valor.replace(/\D/g, '')) };

  const buscar = useMutation({
    mutationFn: () => atendimentoApi.buscarCadastro(criterio()),
    onSuccess: (achado) => {
      setEncontrado(achado);
      setErro(
        achado
          ? null
          : 'Não localizei esse cadastro. Confira o número — ou pode ser alguém que ainda não é cliente.',
      );
    },
    onError: (e) => setErro(e instanceof ErroApi ? e.mensagem : 'Falha ao consultar o sistema.'),
  });

  const vincular = useMutation({
    mutationFn: () => atendimentoApi.vincularCadastro(conversaId, criterio()),
    onSuccess: onVinculado,
    onError: (e) => setErro(e instanceof ErroApi ? e.mensagem : 'Falha ao vincular.'),
  });

  function enviar(e: FormEvent) {
    e.preventDefault();
    setEncontrado(null);
    setErro(null);
    if (!valor.trim()) return;
    buscar.mutate();
  }

  return (
    <Modal aberto titulo="Vincular cadastro" onFechar={onFechar}>
      <form onSubmit={enviar} className="space-y-3">
        <p className="text-sm text-neutral-600 dark:text-neutral-300">
          Este atendimento ainda está sem identificação. Procure o cadastro no sistema para os dados
          do cliente aparecerem aqui e no histórico dele.
        </p>

        <div className="flex gap-1 rounded-lg border border-neutral-300 p-0.5 text-xs dark:border-neutral-700">
          {(
            [
              ['documento', 'CPF / CNPJ'],
              ['codigo', 'Código do cliente'],
            ] as const
          ).map(([v, rotulo]) => (
            <button
              key={v}
              type="button"
              onClick={() => {
                setPor(v);
                setValor('');
                setEncontrado(null);
                setErro(null);
              }}
              className={`flex-1 rounded px-2 py-1.5 transition ${
                por === v
                  ? 'bg-marca-600 font-medium text-white'
                  : 'text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800'
              }`}
            >
              {rotulo}
            </button>
          ))}
        </div>

        <div className="flex gap-2">
          <input
            value={valor}
            autoFocus
            inputMode="numeric"
            onChange={(e) => setValor(e.target.value)}
            placeholder={por === 'documento' ? '000.000.000-00' : 'Código no HubSoft'}
            aria-label={por === 'documento' ? 'CPF ou CNPJ' : 'Código do cliente'}
            className="min-w-0 flex-1 rounded-lg border border-neutral-300 px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-900"
          />
          <Botao type="submit" carregando={buscar.isPending} disabled={!valor.trim()}>
            <Search size={15} /> Procurar
          </Botao>
        </div>

        {erro && <Aviso tipo="erro">{erro}</Aviso>}

        {encontrado && (
          <div className="space-y-2 rounded-lg border border-neutral-200 p-3 dark:border-neutral-700">
            <div>
              <p className="text-sm font-medium">{encontrado.nome}</p>
              <p className="text-xs text-neutral-500">
                {encontrado.documento} · código {encontrado.hubsoftClienteId}
              </p>
            </div>
            {encontrado.pontos.length > 0 ? (
              <ul className="space-y-1">
                {encontrado.pontos.map((p, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-1.5 text-xs text-neutral-600 dark:text-neutral-300"
                  >
                    <Wifi size={12} className="mt-0.5 shrink-0" />
                    <span>
                      {p.rotulo}
                      <span className="text-neutral-400"> · {p.status}</span>
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-neutral-500">Cadastro sem pontos de serviço.</p>
            )}
            <p className="text-[11px] text-neutral-500">
              Confira se é a pessoa com quem você está falando antes de vincular.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Botao variante="secundario" onClick={onFechar} type="button">
            Cancelar
          </Botao>
          <Botao
            type="button"
            disabled={!encontrado}
            carregando={vincular.isPending}
            onClick={() => vincular.mutate()}
          >
            É este cliente, vincular
          </Botao>
        </div>
      </form>
    </Modal>
  );
}
